import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useRef } from "react";

import { usePollGeneration } from "@/api/creativesphere";
import { useUpdatePropagation } from "@/api/propagation";
import { usePilotState } from "@/features/Generations/context/PilotStateContext";
import { ImageOutlinedIcon } from "@/icons";
import {
  GENERATION_STATUS,
  isTerminalGenerationStatus,
} from "@/types/creativesphere";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

// One fixed frame for every state, capped to the viewport so the whole pilot
// stays on screen whatever aspect ratio the blueprint renders at.
const CANVAS_HEIGHT = "clamp(440px, 70vh, 720px)";

const CANVAS_OUTER_SX = {
  border: 1,
  borderColor: "divider",
  borderRadius: 2,
  overflow: "hidden",
  bgcolor: "background.paper",
  height: CANVAS_HEIGHT,
} as const;

const PILOT_IMAGE_SX = {
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: "contain",
} as const;

// Muted, centered frame the empty/loading canvas states render into.
function CanvasFrame({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        px: 6,
        py: 8,
        color: "text.disabled",
        bgcolor: "action.hover",
        height: "100%",
      }}
    >
      {children}
    </Box>
  );
}

function FailedFrame({ message }: { message: string }) {
  return (
    <Box sx={CANVAS_OUTER_SX}>
      <CanvasFrame>
        <ImageOutlinedIcon sx={{ fontSize: 48 }} />
        <Typography variant="body2" color="error">
          {message}
        </Typography>
      </CanvasFrame>
    </Box>
  );
}

export type PilotCanvasProps = {
  siteId: string;
  generationSlug?: string;
  /** The generate request is in flight and hasn't returned a slug yet. */
  isStarting?: boolean;
};

// The Step 2 canvas: empty prompt, then a loader while the generate request is
// in flight (before there's a slug to poll) and the placeholder blurred under a
// "Generating…" overlay while polling, then the real pilot image once ready.
export function PilotCanvas({
  siteId,
  generationSlug,
  isStarting = false,
}: PilotCanvasProps) {
  const {
    data: generation,
    isError,
    isPollingTimedOut,
  } = usePollGeneration(siteId, generationSlug);

  const { pilotState, setPilotState } = usePilotState();
  const imageUrl = generation?.assets[0]?.url;

  const status = generation?.status;
  // A fresh generate request (e.g. Regenerate) is in flight: the polled slug and
  // status still describe the previous run, so keep the canvas in its generating
  // state (neither ready nor failed) until the new slug takes over.
  const isFailed =
    !isStarting &&
    (isError ||
      isPollingTimedOut ||
      status === GENERATION_STATUS.PILOT_FAILED ||
      status === "failed");
  const isReady =
    !isStarting &&
    Boolean(status && !isFailed && isTerminalGenerationStatus(status));

  // The generate response carries no asset yet, so the final render is written
  // to the propagation here, once polling settles; reopening then shows it.
  const { mutate: savePilotImage } = useUpdatePropagation(siteId, {
    onError: (error) =>
      enqueueErrorSnackbar(
        error,
        "Failed to save the pilot image. Please try again.",
      ),
  });
  const propagationSlug = pilotState.propagationSlug;
  const savedImage = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isReady || !imageUrl) return;
    setPilotState((prev) =>
      prev.pilotImageUrl === imageUrl && !prev.pilotRunState
        ? prev
        : { ...prev, pilotImageUrl: imageUrl, pilotRunState: undefined },
    );
    if (!propagationSlug) return;
    const key = `${propagationSlug}:${imageUrl}`;
    if (savedImage.current === key) return;
    savedImage.current = key;
    savePilotImage({
      slug: propagationSlug,
      body: { pilot_image_url: imageUrl },
    });
  }, [isReady, imageUrl, propagationSlug, savePilotImage, setPilotState]);

  if (!generationSlug) {
    // A reopened story has no generation to poll: its stored run state says
    // whether the pilot is still rendering, failed, or ready to show.
    const runState = isStarting ? undefined : pilotState.pilotRunState;
    if (runState === "failed") {
      return (
        <FailedFrame message="Pilot generation failed. Try running it again." />
      );
    }
    const isGenerating = isStarting || runState === "generating";
    const storedImageUrl = isGenerating ? undefined : pilotState.pilotImageUrl;
    if (storedImageUrl) {
      return (
        <Box sx={CANVAS_OUTER_SX}>
          <Box
            component="img"
            src={storedImageUrl}
            alt="Generated pilot"
            sx={PILOT_IMAGE_SX}
          />
        </Box>
      );
    }
    return (
      <Box sx={CANVAS_OUTER_SX}>
        <CanvasFrame>
          {isGenerating ? (
            <>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Generating your pilot…
              </Typography>
            </>
          ) : (
            <>
              <ImageOutlinedIcon sx={{ fontSize: 48 }} />
              <Typography variant="body2" color="text.secondary">
                Your generated pilot will appear here.
              </Typography>
            </>
          )}
        </CanvasFrame>
      </Box>
    );
  }

  if (isFailed) {
    return (
      <FailedFrame
        message={
          isPollingTimedOut
            ? "Pilot generation timed out. Try running it again."
            : "Pilot generation failed. Try running it again."
        }
      />
    );
  }

  return (
    <Box sx={{ ...CANVAS_OUTER_SX, position: "relative" }}>
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt="Generated pilot"
          sx={{
            ...PILOT_IMAGE_SX,
            filter: isReady ? "none" : "blur(6px)",
            transition: "filter 200ms ease",
          }}
        />
      ) : (
        <CanvasFrame>
          <ImageOutlinedIcon sx={{ fontSize: 48 }} />
        </CanvasFrame>
      )}
      {!isReady && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            bgcolor: "rgba(0, 0, 0, 0.35)",
            color: "common.white",
          }}
        >
          <CircularProgress color="inherit" size={28} />
          <Typography variant="body2">Generating your pilot…</Typography>
        </Box>
      )}
    </Box>
  );
}
